import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Button from "react-bootstrap/Button";
import Container from "react-bootstrap/Container";
import Modal from "react-bootstrap/Modal";
import Spinner from "react-bootstrap/Spinner";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { useEffect, useState } from "react";
import {
  fetchallUsersByPractices,
  getUserPracticeById,
  sendPasswordResetEmail,
} from "../../api";
import { EditUser } from "./EditUser";
import { jwtDecode } from "jwt-decode";
import React from "react";

export function ViewUsers({ refresh, onUserUpdate = () => {}}) {
  const [allUsers, setAllUsers] = useState([]);
  const [practices, setPractices] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalShow, setModalShow] = useState(false);
  const [passwordResetModal, setPasswordResetModal] = useState(false);
  const [resettingUserId, setResettingUserId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchUsersAndPractices() {
      const token = sessionStorage.getItem("user");
      if (token) {
        try {
          const decoded = jwtDecode(token);
          const allPrac = await getUserPracticeById(decoded._id);
          setPractices(allPrac);

          const practiceNumbers = allPrac.map(
            (practice) => practice.practiceNumber
          );
          const findAllUsers = await fetchallUsersByPractices(practiceNumbers);
          setAllUsers(findAllUsers);
        } finally {
          setLoading(false);
        }
      }
    }
    fetchUsersAndPractices();
  }, [refresh]);

  const handleEditClick = (userId) => {
    setSelectedUser(userId);
    setModalShow(true);
  };

  async function handleResetPassword(user) {
    setResettingUserId(user._id);
    setSelectedUser(user);
    setPasswordResetModal(true);
    try {
      await sendPasswordResetEmail(user.email);
    } catch (error) {
      console.error("Error sending password reset email:", error);
    } finally {
      setResettingUserId(null);
    }
  }

  const visibleUsers = allUsers
    .filter((u) => !u.role?.includes("SystemAdmin"))
    .sort((a, b) => a.lastName.localeCompare(b.lastName));

  return (
    <Container fluid className="pt-2 acc-list">
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading team members…</p>
        </div>
      ) : visibleUsers.length === 0 ? (
        <p className="text-center">No team members found.</p>
      ) : (
        <Row className="gy-2">
          {visibleUsers.map((user) => (
            <Col md={6} xs={12} key={user._id}>
              <div className="acc-card h-100">
                <div className="acc-card-top">
                  <h6 className="mb-0 text-primary fw-bold">
                    {user.lastName}, {user.firstName}
                  </h6>
                  <OverlayTrigger placement="top" overlay={<Tooltip>Edit user</Tooltip>}>
                    <Button
                      variant="link"
                      className="text-primary text-decoration-none p-0"
                      style={{ width: 24, height: 24 }}
                      onClick={() => handleEditClick(user._id)}
                    >
                      <i className="fa-solid fa-pen-to-square"></i>
                    </Button>
                  </OverlayTrigger>
                </div>

                <div className="acc-card-body">
                  <div className="acc-card-field">
                    <span className="acc-card-label">Email</span>
                    <span className="acc-card-value">{user.email}</span>
                  </div>
                  <div className="acc-card-field">
                    <span className="acc-card-label">Phone Number</span>
                    <span className="acc-card-value">{user.phoneNumber}</span>
                  </div>
                  <div className="acc-card-field">
                    <span className="acc-card-label">Occupation</span>
                    <span className="acc-card-value">{user.occupation}</span>
                  </div>
                  <div className="acc-card-field">
                    <span className="acc-card-label">HPCSA Number</span>
                    <span className="acc-card-value">{user.hpcsa}</span>
                  </div>
                  <div className="acc-card-field">
                    <span className="acc-card-label">Practices</span>
                    <span className="acc-card-value">
                      {user.practice.map((practiceId, index) => (
                        <div key={index}>
                          {practices.find(
                            (p) => p.practiceNumber === practiceId
                          )?.practiceName || "Unknown Practice"}
                        </div>
                      ))}
                    </span>
                  </div>
                  <div className="acc-card-field">
                    <span className="acc-card-label">Roles</span>
                    <span className="acc-card-value">
                      {user.role.map((role, index) => (
                        <div key={index}>{role}</div>
                      ))}
                    </span>
                  </div>
                  <div className="acc-card-field align-items-center">
                    <span className="acc-card-label">Password</span>
                    <span className="acc-card-value">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        disabled={resettingUserId === user._id}
                        onClick={() => handleResetPassword(user)}
                      >
                        {resettingUserId === user._id ? (
                          <Spinner size="sm" animation="border" />
                        ) : (
                          <>
                            <i className="fa-solid fa-key me-2"></i>
                            Reset
                          </>
                        )}
                      </Button>
                    </span>
                  </div>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      )}
      {modalShow && (
        <EditUser
          show={modalShow}
          onHide={() => setModalShow(false)}
          userId={selectedUser}
          onUserUpdate={onUserUpdate}
        />
      )}
      {passwordResetModal && (
        <PasswordResetModal
          show={passwordResetModal}
          onHide={() => setPasswordResetModal(false)}
          user={selectedUser}
        />
      )}
    </Container>
  );
}

function PasswordResetModal({ show, onHide, user }) {
  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header
        closeButton
        style={{ background: "#CFE2FF" }}
        className="rounded-0 text-primary d-flex align-items-center"
      >
        <Modal.Title as="h6" className="m-0">
          Password Reset
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          The password reset request for{" "}
          <strong>
            {user?.firstName} {user?.lastName}
          </strong>{" "}
          has been initiated.
        </p>
        <p>
          An email will be sent shortly with instructions to reset the password.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
